<template>
  <div class="funnel-step-selector">
    <div class="p-field">
      <Dropdown
        v-model="selectedTemplate"
        :options="templateOptions"
        option-label="name"
        :placeholder="t('templateBuilder.funnelStepSelector.placeholder')"
        class="w-full"
        @change="handleTemplateChange"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, PropType, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import { FunnelStep, FunnelTemplate, TemplateEditorState, hasGameWidgetInOtherFunnelTemplates } from '@/composables/template-editor/TemplateEditor';
import { cloneDeep } from 'lodash';

const GAME_RESULT_ID = -99998;

export default defineComponent({
  name: 'FunnelStepSelector',

  components: {
    Dropdown,
  },

  props: {
    funnelSteps: {
      type: Array as PropType<FunnelStep[]>,
      required: true,
    },

    modelValue: {
      type: Object as PropType<FunnelStep | null>,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const selectedTemplate = ref<FunnelTemplate | null>(null);

    // Function to flatten the funnel steps structure for the dropdown
    const flattenFunnelSteps = (steps: FunnelStep[], result: FunnelStep[] = []): FunnelStep[] => {
      steps.forEach((step) => {
        if (step.template && !result.find((r) => String(r.template?.id) === String(step.template?.id))) {
          result.push(step);
        }
        if (step.nextSteps && step.nextSteps.length > 0) {
          flattenFunnelSteps(step.nextSteps, result);
        }
      });
      return result;
    };

    // Options for the dropdown
    const templateOptions = computed(() => {
      const flatSteps = flattenFunnelSteps(props.funnelSteps);
      const nonGameSteps = flatSteps
        .filter((step) => step.template !== null
          && (TemplateEditorState.template?.id !== step.template?.id)
          && !step.template?.isGameFunnel);

      const gameSteps = flatSteps
        .filter((step) => step.template?.isGameFunnel);

      const options: FunnelTemplate[] = nonGameSteps
        .map((step) => step.template) as FunnelTemplate[];

      // "Game result" — single unified option for #redirect_game_result
      // On game template: spins the wheel then redirects to the prize
      // On intermediate template: fetches the prize from backend and redirects
      if (gameSteps.length > 0 || hasGameWidgetInOtherFunnelTemplates()) {
        options.push({
          id: GAME_RESULT_ID,
          name: t('templateBuilder.funnelStepSelector.gameResultStep'),
          type: TemplateEditorState.template?.type || '',
          isGameResult: true,
        });
      }

      return options;
    });

    // Find the complete step from the selected template
    const findFunnelStepByTemplate = (
      steps: FunnelStep[],
      templateId: number,
    ): FunnelStep | null => {
      // eslint-disable-next-line no-restricted-syntax
      for (const step of steps) {
        if (step.template && step.template.id === templateId) {
          return step;
        }
        if (step.nextSteps && step.nextSteps.length > 0) {
          const found = findFunnelStepByTemplate(step.nextSteps, templateId);
          if (found) return found;
        }
      }
      return null;
    };

    // Handle selection change and automatically create a copy
    const handleTemplateChange = () => {
      if (selectedTemplate.value) {
        // Game result: emit a synthetic FunnelStep with the game result flag
        if (selectedTemplate.value.isGameResult) {
          emit('update:modelValue', {
            template: { ...selectedTemplate.value },
            nextSteps: [],
          } as FunnelStep);
          return;
        }

        const foundStep = findFunnelStepByTemplate(
          props.funnelSteps,
          selectedTemplate.value.id,
        );

        if (foundStep) {
          // Create a deep copy of the selected funnel step
          const funnelStepCopy = cloneDeep(foundStep);
          // Emit the copied step via v-model
          emit('update:modelValue', funnelStepCopy);
        }
      } else {
        emit('update:modelValue', null);
      }
    };

    // Reset selection if funnelSteps change
    watch(() => props.funnelSteps, () => {
      selectedTemplate.value = null;
      emit('update:modelValue', null);
    }, { deep: true });

    // Update selectedTemplate when modelValue changes externally
    watch(() => props.modelValue, (newValue) => {
      if (newValue && newValue.template) {
        selectedTemplate.value = newValue.template;
      } else {
        selectedTemplate.value = null;
      }
    });

    return {
      t,
      selectedTemplate,
      templateOptions,
      handleTemplateChange,
    };
  },
});
</script>
